import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Certificate, useDomains } from '../hooks/useDomains';
import { ArrowLeft, Shield, AlertTriangle, RefreshCw, ShieldCheck, Handshake, Zap, CheckCircle, Info, CalendarDays } from 'lucide-react';
import toast from 'react-hot-toast';

type ActiveTabType = 'technical' | 'root' | 'intermediate' | 'tlsspec';

const CertificateDetails = () => {
  const { serial_number } = useParams<{ serial_number: string }>();
  const { domains, isLoading, error: domainsError, refreshDomain } = useDomains();
  const [certificate, setCertificate] = useState<Certificate | null | undefined>(undefined); // undefined for initial, null for not found
  const [activeTab, setActiveTab] = useState<ActiveTabType>('technical');
  // TLS tester API state
  type TlsSpec = {
    domain: string;
    tls12_supported: string[];
    tls13_supported: string[];
  };
  const [tlsSpec, setTlsSpec] = useState<TlsSpec | null>(null);
  const [tlsLoading, setTlsLoading] = useState(false);
  const [tlsError, setTlsError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoading && domains && serial_number) {
      const foundCertificate = domains.find(d => d.serial_number === decodeURIComponent(serial_number));
      setCertificate(foundCertificate || null);
    }
  }, [domains, isLoading, serial_number]);

  // Fetch TLS spec from external API when Technical tab is active and certificate is loaded
  useEffect(() => {
    if (activeTab === 'technical' && certificate?.domain) {
      setTlsLoading(true);
      setTlsError(null);
      // Debug: log payload
      const payload = JSON.stringify({ domain: certificate.domain });
      fetch('https://tls-tester-rr7dbqonrq-uc.a.run.app', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: payload
      })
        .then(async res => {
          if (!res.ok) {
            const text = await res.text();
            throw new Error(`Failed to fetch TLS info: ${text}`);
          }
          return res.json();
        })
        .then(data => {
          setTlsSpec(data);
        })
        .catch(err => {
          setTlsError(err.message || 'Unknown error');
          // Debug: log error
          console.error('TLS fetch error:', err);
        })
        .finally(() => setTlsLoading(false));
    }
  }, [activeTab, certificate?.domain]);

  const handleRefresh = async () => {
    if (certificate?.id) {
      try {
        await refreshDomain(certificate.domain, certificate.serial_number);
        toast.success(`Certificate for ${certificate.domain} refreshed successfully.`);
        // The useDomains hook should update the domains list, which will trigger re-render
      } catch (err) {
        toast.error(`Failed to refresh certificate: ${err instanceof Error ? err.message : 'Unknown error'}`);
      }
    }
  };

  if (isLoading || certificate === undefined) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (domainsError) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-danger-50 border border-danger-200 rounded-md p-4 text-danger-700">
          Error loading domain data: {domainsError}
        </div>
      </div>
    );
  }

  if (!certificate) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 text-center">
        <AlertTriangle className="h-12 w-12 text-warning-500 mx-auto mb-4" />
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Certificate Not Found</h1>
        <p className="text-gray-600 mb-4">The certificate with serial number "{serial_number}" could not be found.</p>
        <Link to="/domains" className="text-primary-600 hover:text-primary-800 font-medium">
          &larr; Back to Domains
        </Link>
      </div>
    );
  }

  const DetailItem: React.FC<{ label: string; value: string | string[] | undefined | null; isLink?: boolean; preformatted?: boolean }> = ({ label, value, isLink, preformatted }) => {
    if (value === undefined || value === null || (Array.isArray(value) && value.length === 0)) return null;

    const renderValue = (val: string) => isLink ? (
      <a href={val.startsWith('http') ? val : `https://${val}`} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">
        {val}
      </a>
    ) : (
      val
    );

    return (
      <div className="py-2 sm:grid sm:grid-cols-3 sm:gap-4">
        <dt className="text-sm font-medium text-gray-500">{label}</dt>
        <dd className={`mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2 ${preformatted ? 'whitespace-pre-wrap' : ''}`}>
          {Array.isArray(value) ? value.map((v, i) => <div key={i}>{renderValue(v)}</div>) : renderValue(value)}
        </dd>
      </div>
    );
  };

  const getStatusIcon = (status: string) => {
    if (status === 'valid') return <CheckCircle className="h-5 w-5 text-accent-600" />;
    if (status === 'warning') return <AlertTriangle className="h-5 w-5 text-warning-600" />;
    if (status === 'expired') return <AlertTriangle className="h-5 w-5 text-danger-600" />;
    return <Info className="h-5 w-5 text-gray-500" />;
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <button
        onClick={() => navigate('/domains')}
        className="mb-6 inline-flex items-center text-primary-600 hover:text-primary-800 font-medium"
      >
        <ArrowLeft className="h-5 w-5 mr-2" />
        Back to Domains
      </button>

      <div className="bg-white shadow-xl rounded-lg overflow-hidden">
        <div className="tabs border-b border-gray-200 flex justify-center">
          {(['technical', 'root', 'intermediate'] as ActiveTabType[]).map(tab => {
            let tabLabel = '';
            switch (tab) {
              case 'technical': tabLabel = 'Technical'; break;
              case 'root': tabLabel = 'Root CA'; break;
              case 'intermediate': tabLabel = 'Intermediate CA'; break;
            }
            return (
              <button
                key={tab}
                className={`tab-button px-4 py-3 -mb-px font-medium text-sm ${activeTab === tab ? 'border-b-2 border-primary-600 text-primary-600' : 'border-b-2 border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
                onClick={() => setActiveTab(tab)}
              >
                {tabLabel}
              </button>
            );
          })}
        </div>

        <div className="p-6">
          {activeTab === 'technical' && (
            <>
              <div className="rounded-lg overflow-hidden">
                <div className={`p-6 ${certificate.status === 'valid' ? 'bg-accent-50' : certificate.status === 'warning' ? 'bg-warning-50' : 'bg-danger-50'}`}> 
                  <div className="flex items-center justify-between">
                    <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                      <Shield className="h-8 w-8 mr-3 text-primary-600" />
                      {certificate.domain}
                    </h1>
                    <button
                      onClick={handleRefresh}
                      className="p-2 rounded-full hover:bg-gray-200 transition-colors"
                      title="Refresh certificate data"
                    >
                      <RefreshCw className="h-5 w-5 text-primary-600" />
                    </button>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">Serial Number: {certificate.serial_number}</p>
                </div>
                <div className="p-6 border-t border-gray-200">
                  <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                    <div className="flex items-center"><CalendarDays className="h-5 w-5 mr-2 text-gray-500" /><strong>Issuer:</strong><span className="ml-2 text-gray-700">{certificate.issuer || 'N/A'}</span></div>
                    <div className="flex items-center"><CalendarDays className="h-5 w-5 mr-2 text-gray-500" /><strong>Expires On:</strong><span className="ml-2 text-gray-700">{certificate.expiry_date}</span></div>
                    <div className="flex items-center">{getStatusIcon(certificate.status || "N/A")}<strong className="ml-2">Status:</strong><span className="ml-2 text-gray-700 capitalize">{certificate.status}</span></div>
                    <div className="flex items-center"><CalendarDays className="h-5 w-5 mr-2 text-gray-500" /><strong>Days Remaining:</strong><span className="ml-2 text-gray-700">{certificate.days_remaining}</span></div>
                    {/* Add more details as needed, e.g., certificate.subject, certificate.algorithm, etc. */}
                  </dl>
                </div>
              </div>
              {/* TLS Specification now always shown under Technical tab */}
              <div className="tab-content-animation mt-8">
                <div className="flex items-start">
                  <div className="mr-6 flex-shrink-0">
                    <Zap className="w-16 h-16 text-primary-500" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-xl font-bold text-gray-900">TLS Specification</h3>
                      <button
                        onClick={() => {
                          if (certificate?.domain) {
                            setTlsLoading(true);
                            setTlsError(null);
                            fetch('https://tls-tester-rr7dbqonrq-uc.a.run.app', {
                              method: 'POST',
                              headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({ domain: certificate.domain })
                            })
                              .then(async res => {
                                if (!res.ok) throw new Error('Failed to fetch TLS info');
                                return res.json();
                              })
                              .then(data => {
                                setTlsSpec(data);
                              })
                              .catch(err => {
                                setTlsError(err.message || 'Unknown error');
                              })
                              .finally(() => setTlsLoading(false));
                          }
                        }}
                        className="p-2 rounded-full hover:bg-gray-200 transition-colors"
                        title="Reload TLS cipher info"
                      >
                        <RefreshCw className="h-5 w-5 text-primary-600" />
                      </button>
                    </div>
                    <p className="text-gray-600 mb-4">Cipher info is fetched live from TLS tester API.</p>
                    {tlsLoading && (
                      <div className="text-gray-500">Loading TLS info...</div>
                    )}
                    {tlsError && (
                      <div className="text-danger-600">Error: {tlsError}</div>
                    )}
                    {tlsSpec && (
                      <div>
                        <div className="mb-4">
                          <div className="font-semibold text-gray-800 mb-2">TLS 1.2 Supported Ciphers</div>
                          {tlsSpec.tls12_supported && tlsSpec.tls12_supported.length > 0 ? (
                            (() => {
                              const weakCount = tlsSpec.tls12_supported.filter(isWeakCipher).length;
                              return (
                                <div>
                                  <div className="mb-2 text-sm text-red-700 font-semibold">
                                    Weak ciphers: {weakCount} {weakCount > 0 && <span className="ml-2 bg-red-100 text-red-700 px-2 py-0.5 rounded text-xs">Review recommended</span>}
                                  </div>
                                  <ul className="list-disc pl-5 space-y-1 text-xs font-mono">
                                    {tlsSpec.tls12_supported.map((cipher: string, idx: number) => (
                                      <li key={idx}>
                                        {isWeakCipher(cipher) ? (
                                          <span className="text-red-600 font-semibold"><code>{cipher}</code> <span className="ml-2 bg-red-100 text-red-700 px-2 py-0.5 rounded text-xs">weak cipher</span></span>
                                        ) : (
                                          <code>{cipher}</code>
                                        )}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              );
                            })()
                          ) : (
                            <div className="text-gray-500">No TLS 1.2 ciphers found.</div>
                          )}
                        </div>
                        <div className="mb-4">
                          <div className="font-semibold text-gray-800 mb-2">TLS 1.3 Supported Ciphers</div>
                          {tlsSpec.tls13_supported && tlsSpec.tls13_supported.length > 0 ? (
                            (() => {
                              const weakCount = tlsSpec.tls13_supported.filter(isWeakCipher).length;
                              return (
                                <div>
                                  <div className="mb-2 text-sm text-red-700 font-semibold">
                                    Weak ciphers: {weakCount} {weakCount > 0 && <span className="ml-2 bg-red-100 text-red-700 px-2 py-0.5 rounded text-xs">Review recommended</span>}
                                  </div>
                                  <ul className="list-disc pl-5 space-y-1 text-xs font-mono">
                                    {tlsSpec.tls13_supported.map((cipher: string, idx: number) => (
                                      <li key={idx}>
                                        {isWeakCipher(cipher) ? (
                                          <span className="text-red-600 font-semibold"><code>{cipher}</code> <span className="ml-2 bg-red-100 text-red-700 px-2 py-0.5 rounded text-xs">weak cipher</span></span>
                                        ) : (
                                          <code>{cipher}</code>
                                        )}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              );
                            })()
                          ) : (
                            <div className="text-gray-500">No TLS 1.3 ciphers found.</div>
                          )}
                        </div>
                      </div>
                    )}
                    {!tlsLoading && !tlsError && !tlsSpec && (
                      <div className="text-gray-500">TLS specification information not available.</div>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
          {activeTab === 'root' && (
            <div className="bg-white rounded-lg overflow-hidden">
              <div className="tab-content-animation">
                <div className="flex items-center mb-4">
                  <ShieldCheck className="w-8 h-8 text-green-600 mr-3" />
                  <h2 className="text-xl font-semibold text-gray-800">Root Certificate Authority</h2>
                </div>
                {certificate.root_cert ? (
                  <dl className="mb-6 pb-4 border-b border-gray-200 last:border-b-0 last:pb-0">
                    {/* <DetailItem label="Subject" value={certificate.root_cert.subject} /> */}
                    <DetailItem label="Organization" value={certificate.root_cert.subject} />
                    {/* <DetailItem label="Issuer Organization" value={certificate.root_cert.issuer} /> */}
                    <DetailItem label="Valid From" value={certificate.root_cert.issued_at} />
                    <DetailItem label="Valid To" value={certificate.root_cert.expires_at} />
                    {/* <DetailItem label="Serial Number" value={certificate.root_cert.serial_number} /> */}
                  </dl>
                ) : (
                  <p className="text-gray-500">Root certificate information not available.</p>
                )}
              </div>
            </div>
          )}
          {activeTab === 'intermediate' && (
            <div className="tab-content-animation">
              <div className="flex items-center mb-4">
                <Handshake className="w-8 h-8 text-blue-600 mr-3" />
                <h2 className="text-xl font-semibold text-gray-800">Intermediate Certificates</h2>
              </div>
              {certificate.intermediate_cert && certificate.intermediate_cert.length > 0 ? (
                certificate.intermediate_cert.map((interCert, index) => (
                  <div key={index} className="mb-6 pb-4 border-b border-gray-200 last:border-b-0 last:pb-0">
                    <h3 className="text-lg font-medium text-gray-700 mb-2">Intermediate Certificate #{index + 1}</h3>
                    <dl className="divide-y divide-gray-100">
                      <DetailItem label="Issuer Organization" value={interCert.issuer} />
                      <DetailItem label="Valid From" value={interCert.issued_at} />
                      <DetailItem label="Valid To" value={interCert.expires_at} />
                      {/* <DetailItem label="Serial Number" value={interCert.serial_number} /> */}
                    </dl>
                  </div>
                ))
              ) : (
                <p className="text-gray-500">No intermediate certificate information available.</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Helper function to detect weak ciphers
function isWeakCipher(cipher: string): boolean {
  const weakPatterns = [
    /rc4/i,
    /des/i,
    /3des/i,
    /md5/i,
    /anon/i,
    /null/i,
    /export/i,
    /cbc/i,
    /psk/i,
    /aNULL/i,
    /eNULL/i,
    /camellia/i,
    /seed/i,
    /idea/i,
    /arcfour/i,
    /rsa$/i,
    /sha$/i,
    /sha1/i,
    /dss/i,
    /srp/i,
    /rc2/i
  ];
  return weakPatterns.some(pattern => pattern.test(cipher));
}

export default CertificateDetails;